// aiden.js — Fellow Aiden integration interface.
//
// This is the seam between the app and the (eventually-real) Fellow API proxy.
// Today it does NOT make any network call. It validates the profile shape,
// stores a copy locally so we can show you what was "pushed", and returns a
// realistic-looking response. The function signatures and return shapes are
// stable — when the Vercel proxy lands, swap the implementation of
// `pushProfileToAiden` to a fetch() against /api/fellow/profile and nothing
// else in the app needs to change.
//
// Why a stub now: the Aiden API is unofficial (reverse-engineered) and
// requires a server-side proxy because:
//   1. Fellow's auth endpoint sets HTTP-only cookies that browser CORS would
//      block, and
//   2. We don't want your Fellow account password living in browser code.
// A Vercel function in /api/fellow.js solves both. See README "Aiden API"
// for the deferred build instructions.

const STUB_KEY = "cucina_v1:aiden_pushed_profiles";

/**
 * Validate that a profile object has every field the Fellow API requires.
 * Throws on failure so callers don't have to remember the schema.
 */
import { apiFetch } from "./api-auth.js";

export function validateAidenProfile(p) {
  if (!p || typeof p !== "object") throw new Error("Profile is empty");
  const errs = [];
  if (typeof p.title !== "string" || !p.title.trim()) errs.push("title is required");
  if (p.title?.length > 30) errs.push("title must be 30 chars or less");
  if (![0, 1].includes(p.profileType)) errs.push("profileType must be 0 (hot) or 1 (cold brew)");
  if (typeof p.ratio !== "number" || p.ratio < 8 || p.ratio > 22) errs.push("ratio must be 8–22");
  if (p.profileType === 0) {
    if (p.bloomEnabled && (typeof p.bloomTemperature !== "number" || p.bloomTemperature < 50 || p.bloomTemperature > 99))
      errs.push("bloomTemperature must be 50–99°C");
    if (p.bloomEnabled && (typeof p.bloomDuration !== "number" || p.bloomDuration < 0 || p.bloomDuration > 120))
      errs.push("bloomDuration must be 0–120s");
    if (p.ssPulsesEnabled) {
      if (!Number.isInteger(p.ssPulsesNumber) || p.ssPulsesNumber < 1 || p.ssPulsesNumber > 6)
        errs.push("ssPulsesNumber must be 1–6");
      if (!Array.isArray(p.ssPulseTemperatures) || p.ssPulseTemperatures.length !== p.ssPulsesNumber)
        errs.push(`ssPulseTemperatures length must equal ssPulsesNumber (${p.ssPulsesNumber})`);
    }
    if (p.batchPulsesEnabled) {
      if (!Array.isArray(p.batchPulseTemperatures) || p.batchPulseTemperatures.length !== p.batchPulsesNumber)
        errs.push(`batchPulseTemperatures length must equal batchPulsesNumber (${p.batchPulsesNumber})`);
    }
  }
  if (errs.length) throw new Error(`Invalid Aiden profile:\n- ${errs.join("\n- ")}`);
  return true;
}

/**
 * Returns whether the real proxy is wired up. False today; the Vercel function
 * will set VITE_AIDEN_PROXY=true at build time once /api/fellow.js exists.
 */
export function aidenProxyEnabled() {
  return import.meta.env.VITE_AIDEN_PROXY === "true";
}

/**
 * Push a profile to the brewer.
 *
 * STUB MODE (today): validates, saves to localStorage, returns a fake response.
 * PROXY MODE (later): POSTs to /api/fellow/profile, the Vercel function
 * authenticates with Fellow's API server-side and creates the profile.
 *
 * @param {Object} aidenProfile - profile shaped like AIDEN_PROFILES values
 * @returns {Promise<{ ok: boolean, mode: "stub"|"proxy", profileId?: string, message: string }>}
 */
export async function pushProfileToAiden(aidenProfile) {
  validateAidenProfile(aidenProfile);

  if (aidenProxyEnabled()) {
    const res = await apiFetch("/api/fellow/profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(aidenProfile),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Aiden proxy returned ${res.status}${text ? `: ${text}` : ""}`);
    }
    const data = await res.json();
    return {
      ok: true,
      mode: "proxy",
      profileId: data.id,
      message: `Pushed to your Aiden as “${data.title || data.id}”`,
    };
  }

  // Stub mode: save the profile locally so the UI can show "what was pushed".
  const stored = readStubStore();
  const fakeId = `p${stored.length}`;
  const record = {
    id: fakeId,
    pushed_at: new Date().toISOString(),
    profile: aidenProfile,
  };
  stored.push(record);
  writeStubStore(stored);

  return {
    ok: true,
    mode: "stub",
    profileId: fakeId,
    message: "Saved locally — copy the values into the Fellow app to use them on your brewer",
  };
}

/**
 * List profiles previously pushed (useful for showing a history/audit in the UI).
 */
export function listPushedProfiles() {
  return readStubStore();
}

/**
 * Fetch the list of profiles currently on the connected Aiden. Returns an
 * empty array in stub mode (no proxy) — callers can use this to detect name
 * conflicts before pushing.
 */
export async function listAidenProfiles() {
  if (!aidenProxyEnabled()) return [];
  const res = await apiFetch("/api/fellow/profiles");
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Aiden proxy returned ${res.status}${text ? `: ${text}` : ""}`);
  }
  return await res.json();
}

/**
 * Update an existing Aiden profile by id (PATCH). Use when there's already
 * a profile on the brewer with the same name and the user wants to overwrite.
 */
export async function updateProfileOnAiden(profileId, aidenProfile) {
  validateAidenProfile(aidenProfile);
  if (!aidenProxyEnabled()) {
    throw new Error("Proxy not enabled — can't update existing Aiden profiles in stub mode");
  }
  // id goes in the query string, not the path — Vercel's catch-all only routes
  // single-segment paths to the proxy (see api/fellow/[...path].js).
  const res = await apiFetch(`/api/fellow/profile?id=${encodeURIComponent(profileId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(aidenProfile),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Aiden proxy returned ${res.status}${text ? `: ${text}` : ""}`);
  }
  return {
    ok: true,
    mode: "proxy",
    profileId,
    message: `Updated "${aidenProfile.title}" on your Aiden`,
  };
}

/**
 * Remove a stored pushed profile by id.
 */
export function removePushedProfile(profileId) {
  const stored = readStubStore().filter(r => r.id !== profileId);
  writeStubStore(stored);
}

/**
 * Delete a profile from the connected Aiden brewer (proxy mode only).
 */
export async function deleteProfileFromAiden(profileId) {
  if (!aidenProxyEnabled()) {
    throw new Error("Proxy not enabled — can't delete Aiden profiles in stub mode");
  }
  const res = await apiFetch(`/api/fellow/profile?id=${encodeURIComponent(profileId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Aiden proxy returned ${res.status}${text ? `: ${text}` : ""}`);
  }
  return { ok: true, profileId };
}

function readStubStore() {
  try {
    const raw = localStorage.getItem(STUB_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeStubStore(arr) {
  try {
    localStorage.setItem(STUB_KEY, JSON.stringify(arr));
  } catch {
    // localStorage full or unavailable — silently ignore. The user just
    // loses the audit trail of pushed profiles, not the brew itself.
  }
}

/**
 * Format a profile as a copyable text block for the Fellow app.
 * Useful in the "stub" UX where the user manually transcribes values.
 */
export function formatProfileAsText(p) {
  // Fellow app + Josh's handoff both work in °F; the API stores °C, so convert.
  const cToF = (c) => (c == null ? "" : Math.round((c * 9) / 5 + 32));
  const lines = [
    `Title: ${p.title}`,
    `Type: ${p.profileType === 1 ? "Cold brew" : "Pour-over"}`,
    `Ratio: 1:${p.ratio}`,
  ];
  if (p.bloomEnabled) {
    lines.push("");
    lines.push("Bloom:");
    lines.push(`  Ratio: ${p.bloomRatio}× dose`);
    lines.push(`  Duration: ${p.bloomDuration}s`);
    lines.push(`  Temp: ${cToF(p.bloomTemperature)}°F`);
  }
  if (p.ssPulsesEnabled) {
    lines.push("");
    lines.push("Single-serve pulses:");
    lines.push(`  Count: ${p.ssPulsesNumber}`);
    lines.push(`  Interval: ${p.ssPulsesInterval}s`);
    lines.push(`  Temps: ${p.ssPulseTemperatures.map(cToF).join("°, ")}°F`);
  }
  if (p.batchPulsesEnabled) {
    lines.push("");
    lines.push("Batch (carafe) pulses:");
    lines.push(`  Count: ${p.batchPulsesNumber}`);
    lines.push(`  Interval: ${p.batchPulsesInterval}s`);
    lines.push(`  Temps: ${p.batchPulseTemperatures.map(cToF).join("°, ")}°F`);
  }
  return lines.join("\n");
}
